import type { FilterId, PrimarySelection } from '../types/filters';
import { getActiveSelections } from '../utils/primaryFilterUtils';
import type { FilterConsistencyState } from './filterGroupConsistency';

/**
 * A generic helper for updating primary filter state within the reducer.
 * It handles the boilerplate of accessing, updating, and synchronizing
 * the pageMap and groupMap.
 *
 * @param state The current filter consistency state.
 * @param payload The action payload containing filterId and pageKey.
 * @param updateFn A callback function that receives the current page-level
 *   primary selections and returns the new, updated list of selections.
 *
 * @returns The new FilterConsistencyState.
 */
export function updatePrimaryFilterState(
  state: FilterConsistencyState,
  payload: { filterId: FilterId; pageKey: string; groupKeyOverride?: string },
  updateFn: (
    currentPageSelections: PrimarySelection[]
  ) => PrimarySelection[] | null
): FilterConsistencyState {
  const { filterId, pageKey } = payload;
  const { activeKey, groupMap, pageMap } = state;

  const groupKey = payload.groupKeyOverride ?? activeKey;

  if (!groupKey) return state;

  const currentGroupMap = groupMap.get(groupKey);
  const currentGroupFilter = currentGroupMap?.get(filterId);
  if (!currentGroupFilter) return state;

  const currentPageSelections = pageMap.get(pageKey)?.get(filterId) || [];

  // Action specific Logic
  const newPageSelections = updateFn(currentPageSelections);

  // If the update function returns null, it means no update should happen.
  if (newPageSelections === null) {
    return state;
  }

  // Update Page Map
  const newPageMapForFilter = new Map(pageMap.get(pageKey) || []);
  newPageMapForFilter.set(filterId, newPageSelections);
  const newPageMap = new Map(pageMap);
  newPageMap.set(pageKey, newPageMapForFilter);

  // Update Group Map
  const newGroupFilter = {
    ...currentGroupFilter,
    primarySelections: newPageSelections,
    selections: getActiveSelections(newPageSelections),
  };
  const newGroupMapForActiveKey = new Map(currentGroupMap);
  newGroupMapForActiveKey.set(filterId, newGroupFilter);
  const newGroupMap = new Map(groupMap);
  newGroupMap.set(groupKey, newGroupMapForActiveKey);

  return {
    ...state,
    groupMap: newGroupMap,
    pageMap: newPageMap,
  };
}
